import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createStructuredSelector } from 'reselect';
import { NextButton } from '../../../widgets/FlightsManager/MobileFragments/NextButton';
import { getTripType, getSegments } from '../../../redux/currentSearch/selectors';
import { getDeparturePrices, getReturnPrices } from '../../../redux/flexibleDates/selectors';
import { CalendarType, SearchSegment, TripType, TripTypeProp } from '../../../models';
import { ConnectedFaresTrackingCalculator } from './FaresTrackingCalculator';
import { ABUtils, CMS, CurrencyUtils, DateUtils, SessionStorageUtils } from '../../../utils';
import { DoneButton } from '../../DatePicker/DoneButton';
import { FaresCalendarLegend } from './FaresCalendarLegend';
import { useFaresCalendarContext } from './FaresCalendarContext';

/* eslint-disable complexity */
export const FaresCalendarBottom = ({ tripType, segments, departurePrices, returnPrices, navigateNext, onMoveFocus, showFaresLegend }) => {
    const { isCssVariationMobile } = ABUtils.getABs();
    const isRoundTrip = tripType === TripType.ROUND_TRIP;
    const show = (tripType === TripType.ONE_WAY && segments[0].date) ||
        (isRoundTrip && segments[0].date && segments[1] && segments[1].date);
    const showDone = show || showFaresLegend;
    const depDateStr = DateUtils.toString(segments[0].date);
    const retDateStr = segments[1] && DateUtils.toString(segments[1].date);
    const price = isRoundTrip ? returnPrices[retDateStr] : departurePrices[depDateStr];
    const showAmount = CurrencyUtils.shouldShowAmount(price) && show;
    const textKey = {
        [TripType.ROUND_TRIP]: 'calendar.prices.from.roundtrip',
        [TripType.ONE_WAY]: 'calendar.prices.from.oneway',
    }[tripType];

    useEffect(() => {
        let priceAccuracy;
        if (show && showAmount) {
            priceAccuracy = price;
        }
        SessionStorageUtils.setJSON('price_accuracy', priceAccuracy);
    }, [price]);

    const wrapperClassName = classNames({
        'odf-box-content-sm': true,
        'prisma-elevation-1': isCssVariationMobile,
    });
    const faresTrackingContext = useFaresCalendarContext();
    const trackType = 'continue_calendar_' + (CalendarType.RETURN === faresTrackingContext.type ? 'ret' : 'dep');

    return (
        <React.Fragment>
            <div className={wrapperClassName}>
                <div className="odf-flex-container-row odf-flex-container-nogutter odf-flex-container-align-baseline">
                    {showFaresLegend && (
                        <div className="odf-flex-item">
                            <div className="odf-space-inner-left-2xl">
                                <FaresCalendarLegend />
                            </div>
                        </div>
                    )}
                    <div className="odf-flex-item">
                        {showAmount && (
                            <div className="odf-text-lg odf-text-right">
                                <span className="odf-strong odf-text-soft"><CMS id={textKey} /> </span>
                                <span className="odf-strong odf-text-mono-color-02">{CurrencyUtils.getFormattedCalendarFares(price)}</span>
                            </div>
                        )}
                    </div>
                    <div className="odf-flex-item odf-flex-item-sm">
                        {showDone && (
                            <ConnectedFaresTrackingCalculator
                                trackType={trackType}
                                minMonthSeen={faresTrackingContext.minMonthSeen}
                                maxMonthSeen={faresTrackingContext.maxMonthSeen}
                            >
                                {({ extraTracking })=>(
                                    <React.Fragment>
                                        { navigateNext ? (
                                            <NextButton extraTracking={extraTracking} fullWidth={false} testId="fares-calendar-next-btn" />
                                        ) : (
                                            <DoneButton extraTracking={extraTracking} action={onMoveFocus} />
                                        )}
                                    </React.Fragment>
                                )}
                            </ConnectedFaresTrackingCalculator>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

FaresCalendarBottom.propTypes = {
    tripType: TripTypeProp.isRequired,
    segments: PropTypes.arrayOf(SearchSegment).isRequired,
    departurePrices: PropTypes.object.isRequired,
    returnPrices: PropTypes.object.isRequired,
    navigateNext: PropTypes.bool.isRequired,
    onMoveFocus: PropTypes.func.isRequired,
    showFaresLegend: PropTypes.bool,
};

FaresCalendarBottom.defaultProps = {
    showFaresLegend: false,
};

const mapStateToPropsSelector = createStructuredSelector({
    tripType: getTripType,
    segments: getSegments,
    departurePrices: getDeparturePrices,
    returnPrices: getReturnPrices,
});

const connected = connect(mapStateToPropsSelector, {})(FaresCalendarBottom);

export { connected as ConnectedFaresCalendarBottom };
